import { useQuery } from "@tanstack/react-query";
import { PodcastService } from "@/lib/podcast/podcast.service";
import type {
	PodcastTranscript,
	TranscriptSegment,
} from "@/lib/podcast/podcast.service";
import { useTranscriptStore } from "@/lib/transcript/transcript.store";

/**
 * A single speaker line with a pre-computed time position within the podcast.
 * Derived by splitting a TranscriptSegment's text into speaker turns and
 * distributing the segment's duration proportionally by character count.
 */
export interface TranscriptLine {
	speaker: string;
	text: string;
	/** Translation for the currently selected language (undefined if none generated yet) */
	translationText?: string;
	/** Global start time (ms) across the full podcast */
	globalStartMs: number;
	/** Estimated duration (ms) for this line */
	durationMs: number;
}

/**
 * Parse "Speaker N: text\n..." into individual speaker turns.
 * Continuation lines (no "Speaker N:" prefix) are merged into the previous turn.
 */
export function parseLines(
	raw: string,
): Array<{ speaker: string; text: string }> {
	const lines = raw.split("\n").filter((l) => l.trim().length > 0);
	const result: Array<{ speaker: string; text: string }> = [];

	for (const line of lines) {
		const match = line.match(/^(Speaker\s+\d+):\s*(.*)/i);
		if (match) {
			result.push({ speaker: match[1] ?? "Speaker", text: match[2] ?? "" });
		} else if (line.trim()) {
			const last = result[result.length - 1];
			if (last) {
				last.text += ` ${line.trim()}`;
			} else {
				result.push({ speaker: "Speaker 1", text: line.trim() });
			}
		}
	}

	return result;
}

/**
 * Expand a single transcript segment into individual speaker lines,
 * distributing the segment duration proportionally by character count.
 *
 * @param segment - The transcript segment to expand.
 * @param translationLang - ISO 639-1 code of the translation to embed in each
 *   line (e.g. "en"). When omitted or not present in segment.translations,
 *   `translationText` on each line will be undefined.
 *
 * This gives sub-segment accuracy (typically ~2–5 second granularity per line)
 * without any changes to TTS chunk sizes or backend data.
 */
export function expandSegmentToLines(
	segment: TranscriptSegment,
	translationLang?: string,
): TranscriptLine[] {
	const parsed = parseLines(segment.text);
	if (parsed.length === 0) return [];

	// Use text length as a proxy for spoken duration within the chunk.
	// Speech rate is roughly constant within a short TTS segment, so this
	// gives a much more accurate approximation than treating the whole chunk
	// as one unit.
	const totalChars = parsed.reduce((sum, l) => sum + l.text.length, 0);

	// Resolve translation for the requested language once per segment
	const rawTranslation = translationLang
		? (segment.translations?.[translationLang] ?? null)
		: null;
	const parsedTranslation = rawTranslation ? parseLines(rawTranslation) : null;

	let cursor = segment.globalStartMs;
	return parsed.map((line, i) => {
		const proportion =
			totalChars > 0 ? line.text.length / totalChars : 1 / parsed.length;
		// Last line gets any rounding remainder to avoid drift
		const isLast = i === parsed.length - 1;
		const endOfSegment = segment.globalStartMs + segment.durationMs;
		const duration = isLast
			? endOfSegment - cursor
			: Math.round(proportion * segment.durationMs);

		const result: TranscriptLine = {
			speaker: line.speaker,
			text: line.text,
			translationText:
				parsedTranslation?.[i]?.text ?? rawTranslation ?? undefined,
			globalStartMs: cursor,
			durationMs: Math.max(duration, 0),
		};
		cursor += result.durationMs;
		return result;
	});
}

/**
 * Fetches the transcript for the given podcast and automatically polls for
 * translation completion when a translation job is in progress.
 *
 * Polling state is read from the transcript store — no manual `pollingLang`
 * argument needed. Components just call `useTranscript(podcastId)`.
 *
 * React Query's cache is the single source of truth for transcript data;
 * both desktop and mobile panels share the same cached result automatically.
 */
export function useTranscript(podcastId: string | null) {
	// Read active translation job for this podcast directly from the store.
	// Using getState() (not a hook subscription) inside refetchInterval is safe
	// because React Query re-evaluates it on every interval tick.
	const pollingLang = useTranscriptStore((s) =>
		podcastId ? (s.translatingByPodcast[podcastId] ?? null) : null,
	);

	return useQuery<PodcastTranscript | null>({
		queryKey: ["transcript", podcastId],
		queryFn: async () => {
			if (!podcastId) return null;
			return PodcastService.getTranscript(podcastId);
		},
		enabled: !!podcastId,
		// Keep data fresh while a translation is in progress; 10 min cache otherwise
		staleTime: pollingLang ? 0 : 1000 * 60 * 10,
		gcTime: 1000 * 60 * 30,
		retry: 2,
		refetchInterval: (query) => {
			// Re-read from store so the interval reacts to store changes even if the
			// component hasn't re-rendered yet
			const activeLang = podcastId
				? useTranscriptStore.getState().translatingByPodcast[podcastId]
				: null;
			if (!activeLang) return false;
			const data = query.state.data;
			if (!data) return 3000;
			const allDone = data.chapters.every(
				(ch) =>
					ch.segments.length === 0 ||
					ch.segments.every((seg) => !!seg.translations?.[activeLang]),
			);
			return allDone ? false : 3000;
		},
	});
}

/**
 * Given the full transcript and the current playback position (in seconds),
 * returns the active speaker line within the transcript.
 *
 * Lines are derived by expanding each segment proportionally by character count,
 * giving much finer granularity than segment-level matching.
 */
export function findActiveLine(
	transcript: PodcastTranscript | null | undefined,
	currentTimeSeconds: number,
): { chapterIndex: number; segmentIndex: number; lineIndex: number } | null {
	if (!transcript) return null;

	const currentMs = currentTimeSeconds * 1000;

	// Track the last segment we've passed without expanding it —
	// only resolve its line count lazily when we actually need it.
	let lastPassedSeg: TranscriptSegment | null = null;
	let lastPassedCi = 0;
	let lastPassedSi = 0;

	for (let ci = 0; ci < transcript.chapters.length; ci++) {
		const chapter = transcript.chapters[ci];
		if (!chapter) continue;

		for (let si = 0; si < chapter.segments.length; si++) {
			const seg = chapter.segments[si];
			if (!seg) continue;

			const segEnd = seg.globalStartMs + seg.durationMs;

			if (currentMs >= segEnd) {
				lastPassedSeg = seg;
				lastPassedCi = ci;
				lastPassedSi = si;
				continue;
			}

			if (currentMs < seg.globalStartMs) {
				// In a gap between segments — resolve the last passed segment
				return resolveLastLine(lastPassedSeg, lastPassedCi, lastPassedSi);
			}

			// We're inside this segment – find the line within it
			const lines = expandSegmentToLines(seg);
			for (let li = 0; li < lines.length; li++) {
				const line = lines[li];
				if (!line) continue;
				const lineEnd = line.globalStartMs + line.durationMs;
				if (currentMs >= line.globalStartMs && currentMs < lineEnd) {
					return { chapterIndex: ci, segmentIndex: si, lineIndex: li };
				}
			}

			// Fallback: current time is in this segment but past all computed lines
			return {
				chapterIndex: ci,
				segmentIndex: si,
				lineIndex: Math.max(0, lines.length - 1),
			};
		}
	}

	// Past all segments — resolve the very last line
	return resolveLastLine(lastPassedSeg, lastPassedCi, lastPassedSi);
}

function resolveLastLine(
	seg: TranscriptSegment | null,
	ci: number,
	si: number,
): { chapterIndex: number; segmentIndex: number; lineIndex: number } | null {
	if (!seg) return null;
	const lineCount = parseLines(seg.text).length;
	return {
		chapterIndex: ci,
		segmentIndex: si,
		lineIndex: Math.max(0, lineCount - 1),
	};
}
